"use client";

import React, { useState, useEffect } from "react";
import { Menu, X, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-6 animate-pulse bg-background">
        <div className="h-6 w-32 bg-muted rounded-none" />
        <div className="h-6 w-24 bg-muted rounded-none" />
        <div className="h-6 w-28 bg-muted rounded-none" />
      </div>;
  }
  return <nav className="flex flex-col p-6 gap-2 bg-background">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="border-b border-border/40 last:border-0">
          <Link to={item.href || "#"} className="flex items-center justify-between py-4 text-lg font-bold text-foreground hover:text-secondary transition-colors" onClick={onClose}>
            {item.label}
            <ChevronRight className="h-4 w-4 text-foreground" />
          </Link>
        </div>)}

      {}
      {menuItems.length === 0 && <>
          <Link to="/OwnerServicesPage" className="flex items-center justify-between py-4 text-lg font-bold text-foreground hover:text-secondary border-b border-border/40" onClick={onClose}>
            Owners <ChevronRight className="h-4 w-4 text-foreground" />
          </Link>
          <Link to="/FindHomePage" className="flex items-center justify-between py-4 text-lg font-bold text-foreground hover:text-secondary border-b border-border/40" onClick={onClose}>
            Renters <ChevronRight className="h-4 w-4 text-foreground" />
          </Link>
          <Link to="/OurStoryPage" className="flex items-center justify-between py-4 text-lg font-bold text-foreground hover:text-secondary border-b border-border/40" onClick={onClose}>
            Our Story <ChevronRight className="h-4 w-4 text-foreground" />
          </Link>
        </>}

      <div className="mt-8">
        <Button asChild className="w-full bg-secondary text-white hover:bg-secondary/90 text-lg h-12 font-bold shadow-sm rounded-none">
          <Link to="/ReachOutPage" onClick={onClose}>
            Contact Us
          </Link>
        </Button>
      </div>
    </nav>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="main-navigation" className={cn("fixed top-0 left-0 w-full z-50 transition-all duration-300", isScrolled ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-border/10 dark:bg-white/95" : "bg-white border-b border-transparent dark:bg-white")}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <Link to="/" className="flex-shrink-0 mr-8">
          <img src="https://possible-pig.10web.cloud/wp-content/uploads/2026/02/Screenshot-2025-04-02-at-9.21.42-AM.png" alt="Taillet Real Estate and Property Management" className="h-14 object-contain" />
        </Link>

        {}
        <div className="hidden md:flex flex-1 justify-center">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="group inline-flex h-9 w-max items-center justify-center rounded-md px-4 py-2 data-[state=open]:hover:bg-accent data-[state=open]:focus:bg-accent data-[state=open]:bg-accent/50 focus-visible:ring-ring/50 outline-none transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 group bg-transparent font-bold text-primary data-[state=open]:text-secondary !text-lg hover:bg-accent focus:bg-accent disabled:pointer-events-none disabled:opacity-50 hover:text-secondary focus:text-secondary">
                  Owners
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid gap-3 p-6 w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr] bg-card">
                    <li className="row-span-3">
                      <NavigationMenuLink asChild>
                        <Link to="/OwnerServicesPage" className="flex h-full w-full select-none flex-col justify-end rounded-none bg-gradient-to-b from-muted/50 to-muted p-6 no-underline outline-none focus:shadow-md hover:bg-muted/80 transition-colors">
                          <div className="mb-2 mt-4 text-lg font-medium text-foreground">
                            Property Management
                          </div>
                          <p className="text-sm leading-tight text-muted-foreground">
                            Comprehensive management services designed to maximize your ROI and minimize stress.
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link to="/OwnerServicesPage" className="block select-none space-y-1 rounded-none p-3 leading-none no-underline outline-none transition-colors hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground">
                          <div className="text-sm font-medium leading-none text-foreground">
                            Owner Services
                          </div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                            Explore our full range of owner-focused solutions.
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link to="/ReachOutPage" className="block select-none space-y-1 rounded-none p-3 leading-none no-underline outline-none transition-colors hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground">
                          <div className="text-sm font-medium leading-none text-foreground">
                            Get a Quote
                          </div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                            Contact us for a personalized management proposal.
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent text-lg font-bold text-primary hover:text-secondary focus:text-secondary data-[state=open]:text-secondary">Renters</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] bg-card">
                    <li>
                      <NavigationMenuLink asChild>
                        <Link to="/FindHomePage" className="block select-none space-y-1 rounded-none p-3 leading-none no-underline outline-none transition-colors hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground">
                          <div className="text-sm font-medium leading-none text-foreground">
                            Find a Home
                          </div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                            Browse our current listings for long-term rentals.
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link to="/FindHomePage" className="block select-none space-y-1 rounded-none p-3 leading-none no-underline outline-none transition-colors hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground">
                          <div className="text-sm font-medium leading-none text-foreground">
                            Short-Term Leases
                          </div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                            Flexible furnished options for temporary stays.
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link to="/ReachOutPage" className="block select-none space-y-1 rounded-none p-3 leading-none no-underline outline-none transition-colors hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground">
                          <div className="text-sm font-medium leading-none text-foreground">
                            Tenant Support
                          </div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                            Submit maintenance requests or contact support.
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                    <li>
                      <NavigationMenuLink asChild>
                        <Link to="/OurStoryPage" className="block select-none space-y-1 rounded-none p-3 leading-none no-underline outline-none transition-colors hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground">
                          <div className="text-sm font-medium leading-none text-foreground">
                            Our Story
                          </div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                            Learn more about our commitment to Abilene.
                          </p>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>

        {}
        <div className="hidden md:flex items-center gap-4">
          <Button asChild className="bg-secondary text-white hover:bg-secondary/90 font-bold shadow-sm rounded-none">
            <Link to="/ReachOutPage">Contact Us</Link>
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-secondary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(true)} aria-label="Open menu">
          <Menu className="h-6 w-6" />
        </Button>
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-[60] bg-background dark:bg-primary flex flex-col animate-in slide-in-from-right duration-300">
          <div className="flex items-center justify-between p-4 border-b border-border/40">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1553193/img/taillet_real_estate_logo.png" alt="Taillet Real Estate" className="h-7 object-contain" />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} className="text-foreground hover:text-secondary hover:bg-transparent" aria-label="Close menu">
              <X className="h-6 w-6" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto">
            <MenuProvider menu_id="47">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>}
    </section>;
}